// lib/transcriptFormat.ts

// --- Color mapping (unchanged) ---
const CHIP_CLASSES = [
  'bg-emerald-100 text-emerald-800 border-emerald-200',
  'bg-sky-100 text-sky-800 border-sky-200',
  'bg-amber-100 text-amber-800 border-amber-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
  'bg-rose-100 text-rose-800 border-rose-200',
  'bg-violet-100 text-violet-800 border-violet-200',
]

function hash(str: string) {
  let h = 0
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0
  return h
}

export function tagClass(tag: string) {
  return CHIP_CLASSES[Math.abs(hash(tag)) % CHIP_CLASSES.length]
}

// --- Parsing helpers ---
// Capture ANY <...> as a tag, then split into base + optional subject.
// Base must be UPPERCASE letters/digits/_/- to avoid false positives.
// Subject can include spaces, dots, slashes, apostrophes, hyphens (e.g., "FINANCIAL LITERACY").
export type Block = {
  base: string // e.g., WORDPHRASEDEFINITIONS
  subject?: string // e.g., FINANCIAL LITERACY
  rawTag: string // full inside of <...>, e.g., WORDPHRASEDEFINITIONS.FINANCIAL LITERACY
  body: string // text until the next tag or end
}

export function parseBlocks(input: string): Block[] {
  const RE_BLOCK = /<([^>]+)>\s*([\s\S]*?)(?=(<[^>]+>\s*)|$)/g
  const blocks: Block[] = []
  let m: RegExpExecArray | null

  while ((m = RE_BLOCK.exec(input)) !== null) {
    const rawTag = m[1].trim()
    const body = m[2].trim()

    // split only on the FIRST dot
    const firstDot = rawTag.indexOf('.')
    const base = (firstDot === -1 ? rawTag : rawTag.slice(0, firstDot)).trim()
    const subject = firstDot === -1 ? undefined : rawTag.slice(firstDot + 1).trim()

    // guard: base must look like a TAG (UPPERCASE + digits/_/-)
    if (!/^[A-Z][A-Z0-9_-]*$/.test(base)) continue

    blocks.push({ base, subject, rawTag, body })
  }

  return blocks
}

// --- Markdown transforms ---
export function toMarkdown(val: string) {
  const blocks = parseBlocks(val)
  if (!blocks.length) return val.trim()

  return blocks
    .map((b) => {
      // Special formatting for definitions:
      // Chip = WORDPHRASEDEFINITIONS, then bold subject, then colon, then body
      if (b.base === 'WORDPHRASEDEFINITIONS' && b.subject) {
        return `**<${b.base}>** **${b.subject}**: ${b.body}`
      }
      // Default: chip shows the full tag (including any dotted sub-tag)
      return `**<${b.rawTag}>** ${b.body}`
    })
    .join('\n\n')
}

// Detect if text already looks like our markdown form (**<...>**)
export function looksLikeMarkdown(s: string) {
  return /\*\*<[^>]+>\*\*/.test(s)
}

// Non-destructive view conversion so chips show on first render
export function toMarkdownForView(s: string) {
  return looksLikeMarkdown(s) ? s : toMarkdown(s)
}
